import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { SITE_ID_FETCH_SUCCESS } from "../../../Reducer/SiteIdSlice";
import { RECTIFIER_ID_FETCH_SUCCESS } from "../../../Reducer/RectifierIDSlice";
import Agent from "../../../Network/Agent";
import API, { cancelAllRequests, createController } from "../../../Network/API";
import RectifierNav from "./RectifierNav";
import RectifierSchematic from "./props/RectifierSchematic";
import RectifierLiveCharts from "./props/RectifierLiveCharts";
import RectifierTelemetryPanel from "./props/RectifierTelemetryPanel";
import RectifierAlarmPanel from "./props/RectifierAlarmPanel";
import "./SiteRectifier.css";

const POLL_INTERVAL_MS = 15000;
const HISTORY_LIMIT = 24;

function RectifierSectionCard({ title, icon, action, children, className = "" }) {
    return (
        <section className={`rectifier-card ${className}`}>
            <div className="rectifier-card__head">
                <h6 className="rectifier-card__title">
                    <i className={icon} />
                    {title}
                </h6>
                {action || null}
            </div>
            <div className="rectifier-card__body">{children}</div>
        </section>
    );
}

function RectifierInfoItem({ label, value, pulse }) {
    return (
        <div className={`rectifier-info-item ${pulse ? "is-pulse" : ""}`}>
            <span className="rectifier-info-item__label">{label}</span>
            <span className="rectifier-info-item__value">{value || "—"}</span>
        </div>
    );
}

function appendHistory(prev, rectifier) {
    const label = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const push = (arr, val) => [...arr, val].slice(-HISTORY_LIMIT);

    return {
        labels: push(prev.labels, label),
        dc_voltage: push(prev.dc_voltage, Number(rectifier?.dc_voltage) || 0),
        dc_current: push(prev.dc_current, Number(rectifier?.dc_current) || 0),
    };
}

const OVERVIEW_FIELDS = [
    { label: "Cluster", value: (data) => data?.rms?.cluster?.name },
    { label: "Zone", value: (data) => data?.rms?.zone?.name },
    { label: "Rectifier SL", value: (data) => data?.rectifier?.rectifier_sl_no },
    { label: "Site ID", value: (data) => data?.rms?.site_id },
    { label: "Easi Site ID", value: (data) => data?.rms?.customer_site_id },
    { label: "Rectifier Name", value: (data) => data?.rectifier?.name },
    { label: "Site Category", value: (data) => data?.rms?.category?.name },
    { label: "Device SL", value: (data) => data?.rms?.device_id },
    { label: "Rectifier Brand", value: (data) => data?.rectifier?.brand_name },
    { label: "Active SIM", value: (data) => data?.rms?.site?.phone_number },
    { label: "RMS Installation", value: (data) => data?.rms?.site?.installation_date },
    { label: "Update Time", value: (data) => data?.rectifier?.update_time },
];

const CONTROLLER_ROWS = [
    { name: "Number of Module", current: (r) => r?.num_module, modify: () => "—", unit: "PCS" },
    { name: "BLVD Setting", current: (r) => r?.blvd_setting, modify: (r) => r?.blvd_value, unit: "V" },
    { name: "LLVD Setting", current: (r) => r?.llvd_setting, modify: (r) => r?.llvd_value, unit: "V" },
    { name: "DC Low Setting", current: (r) => r?.dc_low_setting, modify: (r) => r?.dc_low_value, unit: "V" },
    { name: "Current Limit", current: (r) => r?.cur_limit, modify: (r) => r?.current_limit_value, unit: "%" },
];

const ALARM_ROWS = [
    { title: "Mains Fail", time: "mains_fail_time", status: "mains_fail" },
    { title: "DC Low", time: "dc_low_time", status: "dc_low" },
    { title: "Module Fault", time: "module_fault_time", status: "rectifier_fault" },
    { title: "LLVD", time: "llvd_time", status: "llvd" },
    { title: "Batt/Load Fuse Fault", time: "fuse_fault_time", status: "bat_load_fuse_fault" },
];

export default function SiteRectifier() {
    const ddd = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const [data, setData] = useState();
    const [history, setHistory] = useState({ labels: [], dc_voltage: [], dc_current: [] });
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(location.state?.tab === "graph" ? "graph" : "overview");
    const [lastSync, setLastSync] = useState(null);
    const [dataPulse, setDataPulse] = useState(false);

    const syncData = useCallback((silent = false) => {
        cancelAllRequests();
        const controller = createController();
        const signal = controller.signal;

        if (silent) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        Agent.SITE_DASHBOARD.rectifier_overview(ddd.id, ddd.rid, signal)
            .then((response) => {
                const next = response?.data || null;
                setData(next);
                if (next?.rectifier) {
                    setHistory((prev) => appendHistory(prev, next.rectifier));
                }
                setLastSync(new Date());
                setDataPulse(true);
                window.setTimeout(() => setDataPulse(false), 700);
            })
            .catch((error) => {
                if (error?.name !== "AbortError" && !silent) {
                    toast.error("Failed to load rectifier overview");
                }
            })
            .finally(() => {
                setLoading(false);
                setRefreshing(false);
            });
    }, [ddd.id, ddd.rid]);

    useEffect(() => {
        dispatch(SITE_ID_FETCH_SUCCESS(ddd.id));
        dispatch(RECTIFIER_ID_FETCH_SUCCESS(ddd.rid));
        syncData(false);

        return () => cancelAllRequests();
    }, [ddd.id, ddd.rid, dispatch, syncData]);

    useEffect(() => {
        const timer = window.setInterval(() => syncData(true), POLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [syncData]);

    const handleSubmit = (event) => {
        event.preventDefault();

        const form = event.currentTarget;
        const fd = new FormData(form);
        const numericFields = ["blvd_value", "llvd_value", "dc_low_value", "current_limit_value"];
        const numericPattern = /^\d+(\.\d+)?$/;

        for (const fieldName of numericFields) {
            const fieldValue = String(fd.get(fieldName) ?? "").trim();
            const inputElement = form.elements.namedItem(fieldName);

            if (!fieldValue || !numericPattern.test(fieldValue)) {
                if (inputElement && typeof inputElement.setCustomValidity === "function") {
                    inputElement.setCustomValidity("Please enter a numeric value.");
                    inputElement.reportValidity();
                }
                return;
            }

            if (inputElement && typeof inputElement.setCustomValidity === "function") {
                inputElement.setCustomValidity("");
            }
        }

        setSaving(true);
        fd.append("site_id", ddd.id);
        fd.append("rectifier_id", ddd.rid);

        API.post("/v1/oauth/rectifier_config_setting", fd)
            .then(() => {
                toast.success("Rectifier settings updated");
                setShowModal(false);
                syncData(true);
            })
            .catch(() => {
                toast.error("Failed to update rectifier settings");
            })
            .finally(() => setSaving(false));
    };

    const rectifier = data?.rectifier;
    const updateTime = rectifier?.update_time;
    const isLive = !rectifier?.is_disconnect;
    const activeAlarms = ALARM_ROWS.filter((alarm) => Number(rectifier?.[alarm.status]) === 1).length;
    const lastSyncLabel = lastSync
        ? lastSync.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
        : "—";

    return (
        <div className="rectifier-page">
            <div className="rectifier-page__hero">
                <div className="rectifier-page__hero-info">
                    <RectifierNav activeTab={activeTab} onChange={setActiveTab} />
                    <span className={`rectifier-page__live ${isLive ? "is-online" : ""}`}>
                        <span className="rectifier-live-dot is-live" />
                        LIVE
                    </span>
                    <span className="rectifier-page__pill">
                        <strong>Site:</strong> {data?.rms?.site_id || ddd.id}
                    </span>
                    <span className="rectifier-page__pill">
                        <strong>Rectifier:</strong> {rectifier?.name || ddd.rid}
                    </span>
                    <span className={`rectifier-page__pill ${activeAlarms ? "rectifier-page__pill--alarm" : ""}`}>
                        <strong>Alarms:</strong> {activeAlarms}
                    </span>
                </div>
                <div className="rectifier-page__actions">
                    <span className="rectifier-page__sync">
                        {refreshing ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Syncing...
                            </>
                        ) : (
                            <>
                                <i className="fa-light fa-satellite-dish" />
                                Last sync: {lastSyncLabel}
                            </>
                        )}
                    </span>
                    <button type="button" className="rectifier-page__btn" onClick={() => syncData(true)}>
                        <i className="fa-light fa-arrows-rotate" />
                        Refresh
                    </button>
                    <button type="button" className="rectifier-page__btn rectifier-page__btn--primary" onClick={() => setShowModal(true)}>
                        <i className="fa-light fa-sliders" />
                        Settings
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="rectifier-card">
                    <div className="rectifier-loading">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        <p className="mt-2 mb-0">Loading rectifier telemetry...</p>
                    </div>
                </div>
            ) : activeTab === "graph" ? (
                <section className={`rectifier-monitor rectifier-monitor--graph ${dataPulse ? "is-updating" : ""}`}>
                    <div className="rectifier-monitor__diagram">
                        <RectifierSchematic
                            rectifier={rectifier}
                            siteName={data?.rms?.site?.site_name}
                            updateTime={updateTime}
                        />
                    </div>
                    <div className="rectifier-monitor__charts">
                        <RectifierLiveCharts
                            rectifier={rectifier}
                            history={history}
                            updateTime={updateTime}
                        />
                    </div>
                </section>
            ) : (
                <div className="rectifier-grid">
                        <RectifierSectionCard title="Site Overview" icon="fa-light fa-circle-info">
                            <div className="rectifier-info-grid">
                                {OVERVIEW_FIELDS.map((field) => (
                                    <RectifierInfoItem
                                        key={field.label}
                                        label={field.label}
                                        value={field.value(data)}
                                        pulse={dataPulse}
                                    />
                                ))}
                            </div>
                        </RectifierSectionCard>

                        <RectifierSectionCard
                            title="Controller Setting"
                            icon="fa-light fa-microchip"
                            action={(
                                <button type="button" className="rectifier-page__btn" onClick={() => setShowModal(true)}>
                                    <i className="fa-light fa-pen-to-square" />
                                    Modify
                                </button>
                            )}
                        >
                            <div className="rectifier-table-wrap">
                                <table className="rectifier-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th className="text-center">Current</th>
                                            <th className="text-center">Modify</th>
                                            <th className="text-center">Unit</th>
                                            <th className="text-center">Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {CONTROLLER_ROWS.map((row) => (
                                            <tr key={row.name}>
                                                <th>{row.name}</th>
                                                <td className="text-center">{row.current(rectifier) ?? "—"}</td>
                                                <td className="text-center">{row.modify(rectifier) ?? "—"}</td>
                                                <td className="text-center">{row.unit}</td>
                                                <td className="text-center">{updateTime || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </RectifierSectionCard>

                        <div className="rectifier-split-row">
                            <RectifierSectionCard title="Telemetry Snapshot" icon="fa-light fa-chart-line" className="rectifier-split-row__col">
                                <RectifierTelemetryPanel
                                    rectifier={rectifier}
                                    updateTime={updateTime}
                                    pulse={dataPulse}
                                />
                            </RectifierSectionCard>

                            <RectifierSectionCard title="Alarm Overview" icon="fa-light fa-triangle-exclamation" className="rectifier-split-row__col">
                                <RectifierAlarmPanel alarms={ALARM_ROWS} rectifier={rectifier} />
                            </RectifierSectionCard>
                        </div>
                </div>
            )}

            {showModal ? (
                <div className="rectifier-modal-backdrop" onClick={() => setShowModal(false)}>
                    <div className="rectifier-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="rectifier-modal__head">
                            <h5 className="rectifier-modal__title">Rectifier Setting</h5>
                            <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="rectifier-modal__body">
                                <div className="rectifier-modal__field">
                                    <label>BLVD Setting (V)</label>
                                    <input className="form-control" required name="blvd_value" type="text" defaultValue={rectifier?.blvd_setting} />
                                </div>
                                <div className="rectifier-modal__field">
                                    <label>LLVD Setting (V)</label>
                                    <input className="form-control" required name="llvd_value" type="text" defaultValue={rectifier?.llvd_setting} />
                                </div>
                                <div className="rectifier-modal__field">
                                    <label>DC Low Setting (V)</label>
                                    <input className="form-control" required name="dc_low_value" type="text" defaultValue={rectifier?.dc_low_setting} />
                                </div>
                                <div className="rectifier-modal__field">
                                    <label>Current Limit (%)</label>
                                    <input className="form-control" name="current_limit_value" type="text" defaultValue={rectifier?.cur_limit} />
                                </div>
                            </div>
                            <div className="rectifier-modal__foot">
                                <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-light fa-save me-1" />
                                            Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
